import { Link, NavLink, useNavigate } from 'react-router'
import { ChevronDownIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Logo } from '@/components/Logo'
import { Container } from '@/components/layout'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/features/auth/AuthProvider'
import { isModerator, useAccount } from '@/features/auth/account'
import { signInPath, useCurrentPath } from '@/features/auth/guards'
import { cn } from '@/lib/utils'
import { MAIN_NAV, useTabBarVisible } from './navigation'

function navClass({ isActive }: { isActive: boolean }) {
  return cn(
    'rounded-full px-3.5 py-2 text-sm font-medium transition-[background-color,box-shadow,color] hover:text-foreground',
    isActive ? 'bg-subtle text-foreground shadow-pressed' : 'text-muted-foreground',
  )
}

/**
 * Cabeçalho fixo. No celular, só a marca (a navegação fica na barra inferior); nas telas
 * sem a barra inferior, mostra também "Entrar". No desktop, navegação e conta.
 */
export function SiteHeader() {
  const { user, signOut } = useAuth()
  const { data: account } = useAccount()
  const navigate = useNavigate()
  const current = useCurrentPath()
  const tabBarVisible = useTabBarVisible()

  const handleSignOut = async () => {
    await signOut()
    toast.success('Você saiu da sua conta.')
    void navigate('/')
  }

  return (
    <header className="glass-chrome sticky top-0 z-40 border-b border-white/70">
      <Container className="flex h-14 items-center justify-between gap-4 md:h-16">
        <Link to="/" className="rounded-md" aria-label="Poder Aberto: página inicial">
          <Logo />
        </Link>

        <nav aria-label="Principal" className="hidden items-center gap-1 md:flex">
          {MAIN_NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={navClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Button asChild variant="outline" className="h-10 rounded-full px-4">
            <Link to="/propor">Apresentar proposta</Link>
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 max-w-56 rounded-full">
                  <span className="truncate">{account?.profile.display_name ?? 'Minha conta'}</span>
                  <ChevronDownIcon aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate font-normal text-muted-foreground">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/conta">Minha conta</Link>
                </DropdownMenuItem>
                {account ? (
                  <DropdownMenuItem asChild>
                    <Link to={`/participantes/${account.profile.username}`}>Perfil público</Link>
                  </DropdownMenuItem>
                ) : null}
                {isModerator(account) ? (
                  <DropdownMenuItem asChild>
                    <Link to="/admin">Painel da equipe</Link>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleSignOut}>Sair</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild className="h-10 rounded-full px-5">
              <Link to={signInPath(current)}>Entrar</Link>
            </Button>
          )}
        </div>

        {!user && !tabBarVisible && !/^\/(entrar|cadastro)/.test(current) ? (
          <Button asChild variant="outline" size="sm" className="h-9 rounded-full px-4 md:hidden">
            <Link to={signInPath(current)}>Entrar</Link>
          </Button>
        ) : null}
      </Container>
    </header>
  )
}
